import { forwardRef, useImperativeHandle } from "react";
import toast from "react-hot-toast";

const flattenCircuits = (isAdmin, siteData) => {
  let rows = [];

  function formatForecastDate(timestamp) {
    if (!timestamp) return 0;
    const date = new Date(timestamp * 1000);
    const userLocale = navigator.language;
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString(userLocale, { month: "short" });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const sites = Array.isArray(siteData) ? siteData : [siteData];

  sites.forEach((site) => {
    if (!site?.structures || site.structures.length === 0) return;

    site.structures.forEach((structure) => {
      if (!structure?.circuits || structure.circuits.length === 0) return;

      structure.circuits.forEach((circuit) => {
        if (!circuit) return;

        const row = {
          siteId: site.siteId,
          siteName: site.siteName,
          siteAddressOne: site.addressOne,
          siteAddressTwo: site.addressTwo,
          siteCity: site.city,
          siteState: site.state,
          siteCountry: site.country,
          siteBusinessRegionName: site.businessRegionName,
          siteType: site.type,

          structureId: structure.structureId,
          structureName: structure.structureName,
          structureAddressOne: structure.structureAddressOne ?? "N/A",
          structureAddressTwo: structure.structureAddressTwo ?? "N/A",
          structureCity: structure.structureCity ?? "N/A",
          structureState: structure.structureState ?? "N/A",
          structureCountry: structure.structureCountry ?? "N/A",
          structurePostalCode: structure.structurePostalCode ?? "N/A",
          structureStatus: structure.structureStatus ?? "N/A",

          circuitGmId: circuit.circuitGmId ?? "N/A",
          circuitVendorId: circuit.circuitVendorId ?? "N/A",
          circuitName: circuit.circuitName ?? "N/A",
          status: circuit.status ?? "N/A",
          use: circuit.use ?? "N/A",
          type: circuit.type ?? "N/A",
          category: circuit.category ?? "N/A",
          scope: circuit.scope ?? "N/A",
          portBandwidthKbps: circuit.portBandwidth ?? 0,
          portBandwidthMbps: circuit.portBandwidth
            ? circuit.portBandwidth / 1000
            : 0,
          portBandwidthGbps: circuit.portBandwidth
            ? circuit.portBandwidth / 1000000
            : 0,
          accessBandwidthKbps: circuit.accessBandwidth ?? 0,
          accessBandwidthMbps: circuit.accessBandwidth
            ? circuit.accessBandwidth / 1000
            : 0,
          accessBandwidthGbps: circuit.accessBandwidth
            ? circuit.accessBandwidth / 1000000
            : 0,
          // vcBandwidth: circuit.vcBandwidth ?? 0,

          cutoverDate: formatForecastDate(circuit.cutoverDate),
          forecastDeliveryDate: formatForecastDate(
            circuit.forecastDeliveryDate,
          ),
          actualHandoverDate: formatForecastDate(circuit.actualHandoverDate),
          currentTermEndDate: formatForecastDate(circuit.currentTermEndDate),
          actualBillingStartDate: formatForecastDate(
            circuit.actualBillingStartDate,
          ),
          actualBillingStopDate: formatForecastDate(
            circuit.actualBillingStopDate,
          ),
          requestedDisconnectDate: formatForecastDate(
            circuit.requestedDisconnectDate,
          ),

          redundancyType: circuit.redundancyType ?? "N/A",
          handoffMedia: circuit.handoffType ?? "N/A",
          carrier: circuit.carrier ?? "N/A",
          lec: circuit.lec ?? "N/A",
          diversePhysicalPath: circuit.diversePhysicalPath ?? false,
          diversePop: circuit.diversePop ?? false,
          circuitNote: circuit.circuitNote ?? "N/A",
        };

        if (isAdmin) {
          row.mrcBaseline = circuit.mrcBaseline ?? "N/A";
          row.currency = circuit.currency ?? "N/A";
          row.billToCountry = circuit.billToCountry ?? "N/A";
        }

        rows.push(row);
      });
    });
  });

  return rows;
};

const ExportSiteCircuitsCSV = forwardRef((props, ref) => {
  const exportToCSV = (isAdmin, data) => {
    const flattenedData = flattenCircuits(isAdmin, data);

    if (flattenedData.length === 0) {
      toast.error("No circuit data available to export for selected site.");
      return;
    }

    const headers = Object.keys(flattenedData[0]).join(",");
    const csvRows = flattenedData.map((row) =>
      Object.values(row)
        .map((value) => `"${(value ?? "").toString().replace(/"/g, '""')}"`) // Handle null/undefined values and escape double quotes
        .join(","),
    );

    const csvContent = `data:text/csv;charset=utf-8,${headers}\n${csvRows.join(
      "\n",
    )}`;
    const encodedUri = encodeURI(csvContent).replace(/#/g, "%23");
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "site_circuits.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useImperativeHandle(ref, () => ({
    exportToCSV,
  }));

  return null; // This component doesn't need to render anything to the screen
});

export default ExportSiteCircuitsCSV;
